<template>
  <div v-if="field" class="configurator">
    <field-type :field="field" @input="onInput" />

    <field-configurator-text :field="field" @input="onInput" />
    <field-configurator-options :field="field" @input="onInput" />
    <field-configurator-categories :field="field" @input="onInput" />
    <field-configurator-access :field="field" @input="onInput" />
    <field-configurator-conditional :field="field" @input="onInput" />
    <field-configurator-data-protection :field="field" @input="onInput" />
    <field-configurator-file-types :field="field" @input="onInput" />

    <configuration-tray-buttons
      :disabled="saving || hasErrors"
      :show-delete-button="field.createdAt !== null"
      @saved="save"
      @canceled="onCanceled"
      @deleted="confirmDeletion = true"
    />

    <confirmation-modal
      modal-id="modal-target-delete-field"
      :show-modal="confirmDeletion"
      :confirmation="lang.get('fields.alerts.delete')"
      :confirm-button-label="lang.get('buttons.delete')"
      :cancel-button-label="lang.get('buttons.cancel')"
      @closed="confirmDeletion = false"
      @confirmed="remove"
    />

    <confirmation-modal
      modal-id="modal-target-cancel-field-configuration"
      :show-modal="confirmCancellation"
      :confirmation="lang.get('miscellaneous.unsaved_changes')"
      :confirm-button-label="lang.get('buttons.continue')"
      :cancel-button-label="lang.get('buttons.cancel')"
      @closed="confirmCancellation = false"
      @confirmed="cancel"
    />
  </div>
</template>

<script>
import _ from 'underscore';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import ConfirmationModal from '@/lib/components/Shared/ConfirmationModal';
import ConfigurationTrayButtons from '../ConfigurationTrayButtons';
import FieldType from './FieldType';
import FieldConfiguratorAccess from './FieldConfiguratorAccess';
import FieldConfiguratorCategories from './FieldConfiguratorCategories';
import FieldConfiguratorConditional from './FieldConfiguratorConditional';
import FieldConfiguratorDataProtection from './FieldConfiguratorDataProtection';
import FieldConfiguratorFileTypes from './FieldConfiguratorFileTypes';
import FieldConfiguratorOptions from './FieldConfiguratorOptions';
import FieldConfiguratorText from './FieldConfiguratorText';

export default {
  inject: ['lang', 'currencies'],
  components: {
    ConfigurationTrayButtons,
    ConfirmationModal,
    FieldType,
    FieldConfiguratorAccess,
    FieldConfiguratorCategories,
    FieldConfiguratorConditional,
    FieldConfiguratorDataProtection,
    FieldConfiguratorFileTypes,
    FieldConfiguratorOptions,
    FieldConfiguratorText
  },
  props: {
    field: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      confirmCancellation: false,
      confirmDeletion: false
    };
  },
  computed: {
    ...mapGetters('entryForm', ['compatibleTypes']),
    ...mapState('entryFormConfiguration', [
      'configurationInProgress',
      'saving',
      'hasErrors'
    ])
  },
  created() {
    if (this.field.createdAt) {
      this.preserveField({ ...this.field });
    }

    this.debouncedUpdate = _.debounce(this.updateField, 300);

    this.$emit('created');
  },
  methods: {
    ...mapActions('entryFormConfiguration', [
      'configureField',
      'addField',
      'saveField',
      'deleteField'
    ]),
    ...mapMutations('entryForm', [
      'updateField',
      'removeField',
      'resetFieldValues'
    ]),
    ...mapMutations('entryFormConfiguration', [
      'preserveField',
      'setConfigurationInProgressFlag'
    ]),
    onInput(input) {
      if (!this.configurationInProgress) {
        this.setConfigurationInProgressFlag(true);
      }

      if (
        input.type &&
        this.field.type !== input.type &&
        !this.compatibleTypes(this.field.type).includes(input.type)
      ) {
        this.resetFieldValues(this.field);
      }

      this.debouncedUpdate({ ...this.field, ...input });
    },
    save() {
      if (!this.field.createdAt) {
        this.addField({ field: this.field });
      } else {
        this.saveField({ field: this.field });
      }
    },
    onCanceled() {
      if (this.configurationInProgress) {
        this.confirmCancellation = true;
      } else {
        this.cancel();
      }
    },
    cancel() {
      this.confirmCancellation = false;
      this.$nextTick(() => {
        this.configureField({ slug: null });
      });
    },
    remove() {
      this.confirmDeletion = false;
      this.deleteField({ field: this.field }).then(response => {
        if (response && response.status === 200) {
          this.configureField({ slug: null });
          this.removeField(this.field.slug);
        }
      });
    }
  }
};
</script>

<style scoped>
.configurator {
  margin-bottom: 150px;
}
</style>
